import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useGroups } from '@/hooks/useGroups';
import { useLabOSUsers } from '@/hooks/useLabOSUsers';
import { Users, Plus, Trash2, Edit, UserPlus, ChevronDown, Check, X } from 'lucide-react';
import type { Group } from '@/types/group';

export default function GroupsPage() {
  const { groups, loading, error, createGroup, updateGroup, deleteGroup, addMember, removeMember } = useGroups();
  const { users, loading: usersLoading } = useLabOSUsers();
  const [isCreating, setIsCreating] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newMemberUserIds, setNewMemberUserIds] = useState<Record<string, string>>({}); // Track manual entry input per group
  const [userSearchQuery, setUserSearchQuery] = useState(''); // Filter users in the open dropdown
  const [showUserDropdown, setShowUserDropdown] = useState<string | null>(null); // Track which group's dropdown is open
  const [pendingMembers, setPendingMembers] = useState<Record<string, string[]>>({}); // Track pending member changes per group
  const [isSaving, setIsSaving] = useState<string | null>(null); // Track which group is being saved
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Close the user dropdown when clicking outside of it
  useEffect(() => {
    if (!showUserDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(null);
        setUserSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown]);

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;

    try {
      await createGroup(newGroupName, newGroupDescription, []);
      setNewGroupName('');
      setNewGroupDescription('');
      setIsCreating(false);
    } catch (err) {
      console.error('Failed to create group:', err);
    }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !editingGroup.name.trim()) return;

    try {
      await updateGroup(editingGroup.id, {
        name: editingGroup.name,
        description: editingGroup.description,
      });
      setEditingGroup(null);
    } catch (err) {
      console.error('Failed to update group:', err);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!confirm('Are you sure you want to delete this group?')) return;

    try {
      await deleteGroup(groupId);
    } catch (err) {
      console.error('Failed to delete group:', err);
    }
  };

  // Initialize pending members when opening dropdown or adding manual entry
  const getPendingMembers = (groupId: string): string[] => {
    if (pendingMembers[groupId]) {
      return pendingMembers[groupId];
    }
    const group = groups.find((g) => g.id === groupId);
    return group ? [...group.members] : [];
  };

  // Toggle member selection (doesn't save immediately)
  const handleToggleMember = (groupId: string, userId: string) => {
    const currentPending = getPendingMembers(groupId);
    const newPending = currentPending.includes(userId)
      ? currentPending.filter((id) => id !== userId)
      : [...currentPending, userId];

    setPendingMembers((prev) => ({
      ...prev,
      [groupId]: newPending,
    }));
  };

  // Add member manually
  const handleAddMemberManually = (groupId: string) => {
    const idToAdd = (newMemberUserIds[groupId] || '').trim();
    if (!idToAdd) return;

    const currentPending = getPendingMembers(groupId);
    if (!currentPending.includes(idToAdd)) {
      setPendingMembers((prev) => ({
        ...prev,
        [groupId]: [...currentPending, idToAdd],
      }));
    }
    setNewMemberUserIds((prev) => ({ ...prev, [groupId]: '' }));
  };

  // Save all pending member changes
  const handleSaveMembers = async (groupId: string) => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;

    const newMembers = getPendingMembers(groupId);
    const originalMembers = group.members;

    // Find members to add and remove
    const toAdd = newMembers.filter((id) => !originalMembers.includes(id));
    const toRemove = originalMembers.filter((id) => !newMembers.includes(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      setShowUserDropdown(null);
      return;
    }

    setIsSaving(groupId);
    try {
      // Execute all additions
      for (const userId of toAdd) {
        await addMember(groupId, userId);
      }

      // Execute all removals
      for (const userId of toRemove) {
        await removeMember(groupId, userId);
      }

      // Clear pending changes and close dropdown
      setPendingMembers((prev) => {
        const newPending = { ...prev };
        delete newPending[groupId];
        return newPending;
      });
      setShowUserDropdown(null);
    } catch (err) {
      console.error('Failed to save members:', err);
    } finally {
      setIsSaving(null);
    }
  };

  // Cancel pending changes
  const handleCancelMemberChanges = (groupId: string) => {
    setPendingMembers((prev) => {
      const newPending = { ...prev };
      delete newPending[groupId];
      return newPending;
    });
    setShowUserDropdown(null);
    setNewMemberUserIds((prev) => ({ ...prev, [groupId]: '' }));
  };

  // Check if there are pending changes
  const hasPendingChanges = (groupId: string): boolean => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return false;

    const pending = getPendingMembers(groupId);
    const original = group.members;

    if (pending.length !== original.length) return true;

    return pending.some((id) => !original.includes(id)) || original.some((id) => !pending.includes(id));
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center text-muted-foreground">Loading groups...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Groups</h1>
            <p className="text-muted-foreground mt-1">Manage user groups for workflow access control</p>
          </div>
          <Button onClick={() => setIsCreating(true)} disabled={isCreating}>
            <Plus className="h-4 w-4 mr-2" />
            New Group
          </Button>
        </div>

        {/* Create Group Form */}
        {isCreating && (
          <Card>
            <CardHeader>
              <CardTitle>Create New Group</CardTitle>
              <CardDescription>Define a group to organize users for workflow access</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Group Name</label>
                <Input
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="e.g., Marketing Team"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={newGroupDescription}
                  onChange={(e) => setNewGroupDescription(e.target.value)}
                  placeholder="e.g., Marketing department members"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateGroup} disabled={!newGroupName.trim()}>
                  Create Group
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreating(false);
                    setNewGroupName('');
                    setNewGroupDescription('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Groups List */}
        <div className="grid gap-4">
          {groups.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No groups yet. Create your first group to get started.</p>
              </CardContent>
            </Card>
          ) : (
            groups.map((group) => (
              <Card key={group.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {editingGroup?.id === group.id ? (
                        <div className="space-y-2">
                          <Input
                            value={editingGroup.name}
                            onChange={(e) =>
                              setEditingGroup({
                                ...editingGroup,
                                name: e.target.value,
                              })
                            }
                            placeholder="Group name"
                          />
                          <Input
                            value={editingGroup.description}
                            onChange={(e) =>
                              setEditingGroup({
                                ...editingGroup,
                                description: e.target.value,
                              })
                            }
                            placeholder="Description"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleUpdateGroup}>
                              Save
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingGroup(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <CardTitle className="flex items-center gap-2">
                            <Users className="h-5 w-5 text-muted-foreground" />
                            {group.name}
                          </CardTitle>
                          {group.description && <CardDescription>{group.description}</CardDescription>}
                        </>
                      )}
                    </div>
                    {!editingGroup && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setEditingGroup(group)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteGroup(group.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Members Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium flex items-center gap-2">
                        Members <Badge variant="secondary">{getPendingMembers(group.id).length}</Badge>
                        {hasPendingChanges(group.id) && (
                          <Badge variant="default" className="bg-amber-500">
                            Unsaved
                          </Badge>
                        )}
                      </h4>
                    </div>

                    {/* Member List */}
                    {getPendingMembers(group.id).length > 0 ? (
                      <div className="space-y-2">
                        {getPendingMembers(group.id).map((memberId) => {
                          const user = users.find((u) => u.uid === memberId);
                          const isOriginalMember = group.members.includes(memberId);
                          const isPendingAddition = getPendingMembers(group.id).includes(memberId) && !isOriginalMember;

                          return (
                            <div
                              key={memberId}
                              className={
                                'flex items-center justify-between p-2 rounded-md border transition-all ' +
                                (isPendingAddition
                                  ? 'bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/20'
                                  : 'bg-accent/50 border-border/60')
                              }
                            >
                              <div className="flex-1">
                                {user ? (
                                  <>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium">{user.display_name || user.email}</span>
                                      {isPendingAddition && (
                                        <Badge variant="default" className="text-xs bg-green-500">
                                          New
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="text-xs text-muted-foreground">{user.email}</div>
                                    <div className="text-xs text-muted-foreground font-mono">{memberId.substring(0, 12)}...</div>
                                  </>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-mono text-muted-foreground">{memberId}</span>
                                    {isPendingAddition && (
                                      <Badge variant="default" className="text-xs bg-green-500">
                                        New
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleToggleMember(group.id, memberId)}
                                className="h-7 w-7 p-0 flex-shrink-0"
                                title="Remove member"
                              >
                                <X className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">No members yet</p>
                    )}

                    {/* Add Member */}
                    <div className="mt-3 space-y-2">
                      {/* Available Users Dropdown */}
                      {users.length > 0 && (
                        <div className="relative" ref={showUserDropdown === group.id ? dropdownRef : null}>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setShowUserDropdown(showUserDropdown === group.id ? null : group.id);
                              setUserSearchQuery('');
                            }}
                            className="w-full justify-between"
                            disabled={usersLoading}
                          >
                            <span className="text-xs">{usersLoading ? 'Loading users...' : 'Select from LabOS users'}</span>
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                          {showUserDropdown === group.id && (
                            <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-auto">
                              <div className="sticky top-0 bg-background p-2 border-b">
                                <Input
                                  value={userSearchQuery}
                                  onChange={(e) => setUserSearchQuery(e.target.value)}
                                  placeholder="Search by name, email or UID..."
                                  className="text-sm h-8"
                                  autoFocus
                                />
                              </div>
                              {(() => {
                                const query = userSearchQuery.trim().toLowerCase();
                                const filteredUsers = query
                                  ? users.filter(
                                      (user) =>
                                        (user.display_name || '').toLowerCase().includes(query) ||
                                        user.email.toLowerCase().includes(query) ||
                                        user.uid.toLowerCase().includes(query),
                                    )
                                  : users;

                                if (filteredUsers.length === 0) {
                                  return (
                                    <div className="px-3 py-2 text-xs text-muted-foreground">
                                      {users.length === 0 ? 'No users available' : 'No users match your search'}
                                    </div>
                                  );
                                }

                                return filteredUsers.map((user) => {
                                  const isSelected = getPendingMembers(group.id).includes(user.uid);
                                  return (
                                    <button
                                      key={user.uid}
                                      type="button"
                                      onClick={() => handleToggleMember(group.id, user.uid)}
                                      className={
                                        'w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex items-start gap-2 ' +
                                        (isSelected ? 'bg-violet-50 dark:bg-violet-500/10' : '')
                                      }
                                    >
                                      <div className="flex-shrink-0 mt-0.5">
                                        {isSelected && (
                                          <div className="w-4 h-4 rounded-full bg-violet-500 flex items-center justify-center">
                                            <Check className="h-3 w-3 text-white" />
                                          </div>
                                        )}
                                        {!isSelected && <div className="w-4 h-4 rounded-full border-2 border-border" />}
                                      </div>
                                      <div className="flex-1">
                                        <div className="font-medium">{user.display_name || user.email}</div>
                                        <div className="text-xs text-muted-foreground">{user.email}</div>
                                        <div className="text-xs text-muted-foreground font-mono mt-1">
                                          UID: {user.uid.substring(0, 12)}...
                                        </div>
                                      </div>
                                    </button>
                                  );
                                });
                              })()}
                            </div>
                          )}
                        </div>
                      )}
                      {/* Manual Entry */}
                      <div className="flex gap-2">
                        <Input
                          value={newMemberUserIds[group.id] || ''}
                          onChange={(e) => setNewMemberUserIds((prev) => ({ ...prev, [group.id]: e.target.value }))}
                          placeholder="Or enter Firebase UID manually..."
                          className="text-sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddMemberManually(group.id);
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAddMemberManually(group.id)}
                          disabled={!(newMemberUserIds[group.id] || '').trim()}
                        >
                          <UserPlus className="h-4 w-4 mr-1" />
                          Add
                        </Button>
                      </div>

                      {/* Save/Cancel Buttons */}
                      {hasPendingChanges(group.id) && (
                        <div className="flex gap-2 pt-2 border-t">
                          <Button
                            size="sm"
                            onClick={() => handleSaveMembers(group.id)}
                            disabled={isSaving === group.id}
                            className="flex-1"
                          >
                            {isSaving === group.id ? 'Saving...' : 'Save Changes'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCancelMemberChanges(group.id)}
                            disabled={isSaving === group.id}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
